import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon, MainSection, OuterDiv } from './styles';
import SVGIcon from '../../../../assets/images/svg/SVGIcon';
import ProjectDetails from './projectDetails';
import { useHistory, useLocation } from 'react-router-dom';
import { rootStore } from '../../../../mobx/rootStore';
import { getCustomerList } from '../../../../services/customerServices';
import { getProjectTemplateList } from '../../../../services/settingServices';
import { isEmpty } from '../../../../helpers/common';
import { useMobileDevice } from '../../../../global/useMobile';
import { observer } from 'mobx-react-lite';
import { BUTTON_TYPE, CREATE_PROJECT_STEP, ICON_SIZE } from '../../../../global/constants';
import TeamMembers from './teamMember';
import ResponsiveHeader from '../../../../component/responsiveHeader';
import BottomSubNavigation from '../../../../component/bottomNavigation/subNavigation';
import ProjectTemplate from './projectTemplate';
import RightSection from './rightSection';
import { ButtonSection } from './projectDetails/styles';
import Button from '../../../../component/button';
import recommendedTemplates from '../../../../sample_data/projectTemplates.json';
import { formatToProjectTemplateStore } from '../../../../mobx/common';
import useKeyboardVisibility from '../../../../global/useKeyboard';
import { ROUTES } from '../../../../routes/urls';

function CreateProjectStep2() {
  //Other variables
  const history = useHistory();
  const isMobile = useMobileDevice();
  const location = useLocation();
  const isKeyboardVisible = useKeyboardVisibility();

  // Check for select-template query parameter
  const searchParams = new URLSearchParams(location.search);
  const hasSelectTemplate = searchParams.has('select-template');

  //State variables
  const [createProjectStep, setCreateProjectStep] = useState(
    hasSelectTemplate ? CREATE_PROJECT_STEP.SELECT_TEMPLATE : CREATE_PROJECT_STEP.ADD_INFO
  );
  //Mobx store variables
  const { workspaceStore, projectStore } = rootStore;
  const { currentWorkspace } = workspaceStore;
  const { createProjectInput } = projectStore;

  //Load Initial Data
  const loadData = useCallback(async () => {
    if (!isEmpty(currentWorkspace?.id)) {
      let promises = [getCustomerList()];
      if (isEmpty(createProjectInput?.id) && hasSelectTemplate) {
        promises.push(getProjectTemplateList(currentWorkspace?.id));
        const formattedData = formatToProjectTemplateStore(recommendedTemplates, true);
        rootStore.projectTemplateStore.setRecommendedTemplates(formattedData);
      }
      await Promise.all(promises);
    }
  }, [currentWorkspace?.id, createProjectInput?.id, hasSelectTemplate]);

  useEffect(() => {
    loadData();
    return () => {
      createProjectInput?.clear();
    };
  }, [createProjectInput, loadData]);

  const onClickBack = useCallback(() => {
    if (createProjectStep === CREATE_PROJECT_STEP.ADD_MEMBER) {
      setCreateProjectStep(CREATE_PROJECT_STEP.ADD_INFO);
    } else if (createProjectStep === CREATE_PROJECT_STEP.ADD_INFO && hasSelectTemplate) {
      setCreateProjectStep(CREATE_PROJECT_STEP.SELECT_TEMPLATE);
    } else if (createProjectStep === CREATE_PROJECT_STEP.TEMPLATE_PREVIEW) {
      setCreateProjectStep(CREATE_PROJECT_STEP.SELECT_TEMPLATE);
    } else {
      history.goBack();
    }
  }, [createProjectStep, hasSelectTemplate, history]);

  const onClickCancel = useCallback(() => {
    history.push(ROUTES.ALL_PROJECT);
  }, [history]);

  const renderCloseIcon = useMemo(() => {
    return (
      <Icon role='button' className='closeIcon' onClick={onClickCancel}>
        <SVGIcon
          name='new-close-icon'
          width={ICON_SIZE.XL}
          height={ICON_SIZE.XL}
          viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
          fill='var(--text-secondary)'
        />
      </Icon>
    );
  }, [onClickCancel]);

  const onClickUseTemplate = () => {
    setCreateProjectStep(CREATE_PROJECT_STEP.ADD_INFO);
  };

  const renderLeftSection = () => {
    return createProjectStep === CREATE_PROJECT_STEP.SELECT_TEMPLATE ? (
      // Add Template component here
      <ProjectTemplate
        onClickTemplate={() => setCreateProjectStep(CREATE_PROJECT_STEP.TEMPLATE_PREVIEW)}
        onClickUseTemplate={onClickUseTemplate}
      />
    ) : createProjectStep === CREATE_PROJECT_STEP.ADD_INFO ? (
      <ProjectDetails onClickCreate={() => setCreateProjectStep(CREATE_PROJECT_STEP.ADD_MEMBER)} />
    ) : createProjectStep === CREATE_PROJECT_STEP.TEMPLATE_PREVIEW ? (
      isMobile && <RightSection currentStep={createProjectStep} />
    ) : (
      <TeamMembers />
    );
  };

  return (
    <div className='desktop-app-file-view-wrapper'>
      <OuterDiv
        isKeyboardVisible={isKeyboardVisible}
        applyScrolling={createProjectStep === CREATE_PROJECT_STEP.SELECT_TEMPLATE ? true : false}
        className='desktop-app-file-view'>
        {isMobile ? (
          <ResponsiveHeader
            title={
              createProjectStep === CREATE_PROJECT_STEP.ADD_INFO
                ? 'Add project details'
                : createProjectStep === CREATE_PROJECT_STEP.SELECT_TEMPLATE
                ? 'Project templates'
                : createProjectStep === CREATE_PROJECT_STEP.TEMPLATE_PREVIEW
                ? createProjectInput?.selectedTemplate?.name || ''
                : 'Team members'
            }
            noMargin={true}
            leftElement={renderCloseIcon}
            isProjectDetail={createProjectStep === CREATE_PROJECT_STEP.TEMPLATE_PREVIEW ? true : false}
            isTemplatePreview={true}
            applyDottedText={createProjectStep === CREATE_PROJECT_STEP.TEMPLATE_PREVIEW ? true : false}
          />
        ) : (
          <>
            <Icon role='button' className='backIcon' onClick={onClickBack}>
              <SVGIcon
                name='back-navigation-arrow'
                width={ICON_SIZE.XL}
                height={ICON_SIZE.XL}
                viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                fill='var(--text-secondary)'
              />
            </Icon>
            {renderCloseIcon}
          </>
        )}
        {isMobile && renderLeftSection()}
        {!isMobile && (
          <MainSection className='desktop-create-project-main-div'>
            {renderLeftSection()}
            <RightSection currentStep={createProjectStep} />
          </MainSection>
        )}
      </OuterDiv>
      {isMobile && createProjectStep === CREATE_PROJECT_STEP.TEMPLATE_PREVIEW && (
        <ButtonSection isProjectTemplatePreview>
          <Button
            title='Use template'
            variant={BUTTON_TYPE.PRIMARY}
            disabled={!createProjectInput?.templateId}
            onClick={onClickUseTemplate}
          />
        </ButtonSection>
      )}
      <BottomSubNavigation handleBackIcon={onClickBack} />
    </div>
  );
}

export default observer(CreateProjectStep2);
